import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { IconPickerPopover, RenderIcon } from "./IconPickerPopover";
import {
  TierPricing,
  TierKey,
  ModulePricing,
  createBlankTier,
} from "../types";

interface TierPricingEditorProps {
  pricing: TierPricing;
  modulePricing: ModulePricing;
  onChange: (pricing: TierPricing) => void;
}

export const TierPricingEditor = ({
  pricing,
  modulePricing,
  onChange,
}: TierPricingEditorProps) => {
  const handleChange = (tier: TierKey, field: string, value: number) => {
    onChange({
      ...pricing,
      [tier]: { ...pricing[tier], [field]: value },
    });
  };

  const handleModuleToggle = (tier: TierKey, moduleKey: string) => {
    const current = pricing[tier].includedModules || [];
    const next = current.includes(moduleKey)
      ? current.filter((m) => m !== moduleKey)
      : [...current, moduleKey];
    onChange({
      ...pricing,
      [tier]: { ...pricing[tier], includedModules: next },
    });
  };

  const handleAddTier = () => {
    const key = `tier_${Date.now()}`;
    onChange({ ...pricing, [key]: createBlankTier("Nieuw tier") });
  };

  const handleDeleteTier = (tier: TierKey) => {
    const next = { ...pricing };
    delete next[tier];
    onChange(next);
  };

  const handleMoveTier = (tier: TierKey, direction: -1 | 1) => {
    const keys = Object.keys(pricing);
    const idx = keys.indexOf(tier);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= keys.length) return;
    // Swap by rebuilding the object in new order
    const newKeys = [...keys];
    [newKeys[idx], newKeys[newIdx]] = [newKeys[newIdx], newKeys[idx]];
    const reordered: TierPricing = {};
    for (const k of newKeys) reordered[k] = pricing[k];
    onChange(reordered);
  };

  const handleTierField = (tier: TierKey, field: string, value: string) => {
    onChange({ ...pricing, [tier]: { ...pricing[tier], [field]: value } });
  };

  const enabledModules = Object.keys(modulePricing).filter(
    (k) => modulePricing[k].enabled
  );

  return (
    <div className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {(Object.keys(pricing) as TierKey[]).map((tier, tierIdx, tierArr) => {
        const config = pricing[tier];
        const isFree = !!config.isFree;
        const isFirst = tierIdx === 0;
        const isLast = tierIdx === tierArr.length - 1;

        return (
          <Card
            key={tier}
            className={
              config.isPopular
                ? "border-primary/40 ring-1 ring-primary/10"
                : ""
            }
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <IconPickerPopover value={config.icon} onChange={(v) => handleTierField(tier, "icon", v)} size="sm" />
                  <div className="min-w-0">
                    <Input value={config.label}
                      onChange={(e) => handleTierField(tier, "label", e.target.value)}
                      className="h-6 text-sm font-bold p-0 border-none shadow-none bg-transparent" />
                    <Input value={config.description}
                      onChange={(e) => handleTierField(tier, "description", e.target.value)}
                      placeholder="Beschrijving..."
                      className="h-5 text-[10px] text-neutral-secondary-mid p-0 border-none shadow-none bg-transparent" />
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {!isFirst && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-neutral-secondary-mid hover:text-primary"
                      onClick={() => handleMoveTier(tier, -1)} title="Naar links">
                      <ArrowLeft size={12} />
                    </Button>
                  )}
                  {!isLast && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-neutral-secondary-mid hover:text-primary"
                      onClick={() => handleMoveTier(tier, 1)} title="Naar rechts">
                      <ArrowRight size={12} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm"
                    className="h-6 w-6 p-0 text-destructive/40 hover:text-destructive"
                    onClick={() => handleDeleteTier(tier)}>
                    <Trash size={12} />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {isFree ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs text-neutral-secondary-mid">Max units</label>
                      <Input type="number" step="1" min="1"
                        value={config.freeMaxUnits ?? 3}
                        onChange={(e) => handleChange(tier, "freeMaxUnits", parseInt(e.target.value) || 3)}
                        className="w-20 h-7 text-xs text-right" />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs text-neutral-secondary-mid">Max m&sup2;</label>
                      <Input type="number" step="100" min="0"
                        value={config.freeMaxM2 ?? 500}
                        onChange={(e) => handleChange(tier, "freeMaxM2", parseInt(e.target.value) || 500)}
                        className="w-20 h-7 text-xs text-right" />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs text-neutral-secondary-mid">Max gebruikers</label>
                      <Input type="number" step="1" min="1"
                        value={config.includedUsers}
                        onChange={(e) => handleChange(tier, "includedUsers", parseInt(e.target.value) || 1)}
                        className="w-20 h-7 text-xs text-right" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs text-neutral-secondary-mid">
                        Basisprijs /mnd
                      </label>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-neutral-secondary-mid">&euro;</span>
                        <Input type="number" step="1" min="0" value={config.basePrice}
                          onChange={(e) => handleChange(tier, "basePrice", parseFloat(e.target.value) || 0)}
                          className="w-20 h-7 text-xs text-right" />
                      </div>
                    </div>

                  </>
                )}

                {!isFree && (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs text-neutral-secondary-mid">
                        Incl. gebruikers
                      </label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={config.includedUsers}
                        onChange={(e) =>
                          handleChange(
                            tier,
                            "includedUsers",
                            parseInt(e.target.value) || 1
                          )
                        }
                        className="w-20 h-7 text-xs text-right"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs text-neutral-secondary-mid">
                        Per extra gebruiker
                      </label>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-neutral-secondary-mid">
                          &euro;
                        </span>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={config.pricePerExtraUser}
                          onChange={(e) =>
                            handleChange(
                              tier,
                              "pricePerExtraUser",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-20 h-7 text-xs text-right"
                        />
                        <span className="text-[9px] text-neutral-secondary-mid">
                          /mnd
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Module assignment */}
              {enabledModules.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-[10px] font-semibold text-neutral-secondary-mid uppercase tracking-wider mb-2">
                      Inbegrepen modules
                    </p>
                    <div className="space-y-1.5">
                      {enabledModules.map((modKey) => {
                        const mod = modulePricing[modKey];
                        const included = (
                          config.includedModules || []
                        ).includes(modKey);
                        return (
                          <label
                            key={modKey}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Checkbox
                              checked={included}
                              onCheckedChange={() =>
                                handleModuleToggle(tier, modKey)
                              }
                            />
                            <span className="flex items-center gap-1.5 text-[11px]">
                              <RenderIcon value={mod.icon} size={14} />
                              <span
                                className={
                                  included
                                    ? "text-foreground"
                                    : "text-neutral-secondary-mid"
                                }
                              >
                                {mod.label}
                              </span>
                              {included && (
                                <span className="text-[9px] text-neutral-secondary-mid">
                                  +&euro;{modulePricing[modKey].pricePerUnit}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
    <Button variant="outline" size="sm" onClick={handleAddTier} className="gap-1.5 text-xs">
      <Plus size={12} /> Tier toevoegen
    </Button>
    </div>
  );
};
